/**
 * Shared counting primitives: the art piece (sticker / bare cutout), number
 * cards, frosted panels, the running-count readout, countable piece grids,
 * and the small hooks every counting engine needs (complete-once, wrong
 * flash, drag engine + tap guard). Everything sizes off `--tile`, the
 * per-piece size the stage computes from the arena it gives us.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../theme";
import { COUNTING_PIECE, learnAssetSrcSet } from "../media/learnAssets";
import { useDragEngine } from "../interaction/useDragEngine";
import { countingSpokenWord } from "../../../data/countingLadderCatalog";

export { COUNTING_PIECE };

// ── Tokens ────────────────────────────────────────────────────────────────

/** Frosted white panel that stays legible over the world art. */
export const COUNTING_PANEL = {
  background: "rgba(255,255,255,0.92)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  borderRadius: LEARN.radius.lg,
  boxShadow: LEARN.shadow.card,
  border: `2px solid ${LEARN.color.line}`,
};

export function hexAlpha(hex, alpha) {
  const raw = String(hex || LEARN.color.blue).replace("#", "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function darkenHex(hex, amount = 0.32) {
  const raw = String(hex || LEARN.color.blue).replace("#", "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  const n = parseInt(full, 16);
  const ch = (v) => Math.max(0, Math.round(v * (1 - amount))).toString(16).padStart(2, "0");
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}

/** Spring used for pieces popping in. */
export const POP_SPRING = { type: "spring", stiffness: 420, damping: 22 };

/** White card face with the faintest cool fall-off, so stacked cards read as separate. */
export const CARD_FACE = `linear-gradient(180deg, ${LEARN.color.white} 0%, ${hexAlpha(LEARN.color.blueSoft, 0.55)} 100%)`;

/** The 3D bottom edge of a resting (idle) card, bin or pane. */
export const EDGE_IDLE = hexAlpha(LEARN.color.ink, 0.16);

/** Warm oak the supply shelves are cut from (the counting course's deep sun). */
const WOOD = LEARN.color.sunDeep;

// ── Piece sizing ──────────────────────────────────────────────────────────

/** Smallest a piece may ever render (a forgiving finger target). */
export const PIECE_MIN_PX = 56;

/** Generous breathing room between pieces of a set. */
export const PIECE_GAP = "calc(var(--tile) * 0.35)";

/**
 * How big one piece of a set should be, in tiles: a handful of ducks fills
 * the panel, a set of ten is still chunky, a teen set is one tile each.
 */
export function pieceScale(count) {
  if (count <= 5) return 1.8;
  if (count <= 10) return 1.3;
  return 1;
}

/** Gap ratio (of a tile) between the pieces of a set of `count`. */
export function pieceGapRatio(count) {
  return count > 10 ? 0.2 : 0.35;
}

/** CSS size for a piece of a set of `count` when nothing has been measured yet. */
export function pieceSizeCss(count, scale = pieceScale(count)) {
  return `max(${PIECE_MIN_PX}px, calc(var(--tile) * ${scale}))`;
}

/**
 * A supply tray under the play panel: pieces as big as their set allows,
 * shrunk so `count` of them sit across the arena in one row, never smaller
 * than `minScale` tiles (a nine-berry tray wraps to two rows rather than
 * shrinking to a bead) and never taller than a fifth of the arena.
 */
export function trayPieceSizeCss(count, { maxScale = 1.8, minScale = 0.8, gapRatio = 0.15, inset = 48 } = {}) {
  const s = Math.min(pieceScale(count), maxScale);
  const n = Math.max(1, count);
  const oneRow = `calc((var(--arena-w, 760px) - ${inset}px - calc(var(--tile) * ${gapRatio}) * ${n - 1}) / ${n})`;
  return `max(${PIECE_MIN_PX}px, calc(var(--tile) * ${minScale}), min(calc(var(--tile) * ${s}), ${oneRow}, calc(var(--arena-h, 640px) * 0.2)))`;
}

/**
 * Biggest square that lets `count` pieces sit in a `width` x `height` box in
 * some column count, `gap` apart, never over `max`. Ties prefer a full last
 * row (two-and-two beats three-and-one), then fewer rows. Returns null when
 * there is no box yet.
 */
export function fitPieceSize({ width, height, count, gap = 0, max = Infinity, min = PIECE_MIN_PX, maxColumns }) {
  if (!(width > 0) || !(height > 0) || !(count > 0)) return null;
  const top = Math.max(1, Math.min(count, maxColumns || count));
  let best = 0;
  let bestCols = top;
  for (let cols = top; cols >= 1; cols -= 1) {
    const rows = Math.ceil(count / cols);
    const s = Math.min(max, (width - gap * (cols - 1)) / cols, (height - gap * (rows - 1)) / rows);
    const tieButEven = Math.abs(s - best) < 0.5 && count % cols === 0 && count % bestCols !== 0;
    if (s > best || tieButEven) { best = s; bestCols = cols; }
  }
  if (best < min) {
    // Too tight to honour the minimum in a neat grid: keep the floor and let
    // the pieces wrap as the width allows (the root scrolls if it must).
    return { size: min, cols: Math.max(1, Math.min(top, Math.floor((width + gap) / (min + gap)))) };
  }
  return { size: Math.floor(best), cols: bestCols };
}

/**
 * Measures the box you attach `ref` to: `{ width, height, tile }` in px, or
 * null before the first measurement (and in jsdom, which has no
 * ResizeObserver). `tile` is the resolved `--tile`, so callers can turn
 * tile-relative paddings into px and fit content exactly.
 */
export function useMeasuredBox() {
  const ref = useRef(null);
  const [box, setBox] = useState(null); // { width, height, tile }
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const read = () => {
      const rect = el.getBoundingClientRect();
      const tile = parseFloat(getComputedStyle(el).getPropertyValue("--tile")) || 64;
      const next = { width: Math.floor(rect.width), height: Math.floor(rect.height), tile };
      setBox((prev) => (prev && prev.width === next.width && prev.height === next.height && prev.tile === next.tile ? prev : next));
    };
    read();
    // --tile settles after the stage measures its arena; re-read once it has.
    const raf = requestAnimationFrame(() => requestAnimationFrame(read));
    const late = setTimeout(read, 250);
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); clearTimeout(late); };
  }, []);
  return { ref, box };
}

/**
 * Measures the box you attach `ref` to and returns the piece size (a CSS
 * length) plus the column count that fills it. Before the first measurement
 * (and in jsdom) it falls back to `scale x --tile`.
 */
export function useFitPieceSize({ count, scale, gapRatio, maxColumns, minPx = PIECE_MIN_PX } = {}) {
  const { ref, box } = useMeasuredBox();
  const s = scale ?? pieceScale(count);
  const ratio = gapRatio ?? pieceGapRatio(count);
  const fit = box ? fitPieceSize({ width: box.width, height: box.height, count, gap: box.tile * ratio, max: box.tile * s, min: minPx, maxColumns }) : null;
  return {
    ref,
    size: fit ? `${fit.size}px` : pieceSizeCss(count, s),
    cols: fit ? fit.cols : maxColumns || null,
    gap: box ? `${Math.round(box.tile * ratio)}px` : `calc(var(--tile) * ${ratio})`,
    measured: Boolean(fit),
  };
}

/**
 * The box a set of pieces lives in: fills whatever flex/grid cell it is
 * given and centres its content. `cols` (with `size` + `gap`) caps the row
 * width so the last row of a wrapped set stays centred, dice-style.
 */
export function PieceField({ fieldRef, children, size, gap, cols, style, innerStyle, ...rest }) {
  const rowWidth = cols && size ? `calc(${size} * ${cols} + ${gap} * ${Math.max(0, cols - 1)})` : undefined;
  return (
    <div
      ref={fieldRef}
      {...rest}
      style={{
        flex: "1 1 0%", minHeight: 0, minWidth: 0, width: "100%", height: "100%", alignSelf: "stretch",
        display: "flex", alignItems: "center", justifyContent: "center", position: "relative", ...style,
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", alignContent: "center", gap, maxWidth: rowWidth || "100%", ...innerStyle }}>
        {children}
      </div>
    </div>
  );
}

export const PIECE_NOUN = {
  duck: "duck", sheep: "sheep", star: "star", leaf: "leaf", bead: "bead", pebble: "pebble",
  rabbit: "rabbit", flower: "flower", blueberry: "blueberry", cube: "block", rod: "rod",
  crayon: "crayon", drum: "drum", step: "stone", basket: "basket", cloth: "cloth", domino: "domino",
};

// ── Global styles (injected once by CountingRoot) ─────────────────────────

const STYLE_ID = "counting-engine-styles";
const STYLE = `
@keyframes counting-float { from { transform: translateY(2px); } to { transform: translateY(-5px); } }
.counting-float { animation: counting-float 1.9s ease-in-out infinite alternate; }
@keyframes counting-shake { 0%,100% { transform: translateX(0); } 20% { transform: translateX(-7px); } 40% { transform: translateX(7px); } 60% { transform: translateX(-4px); } 80% { transform: translateX(4px); } }
.counting-shake { animation: counting-shake 0.42s ease; }
@keyframes counting-pulse { 0% { transform: scale(1); } 40% { transform: scale(1.16); } 100% { transform: scale(1); } }
.counting-pulse { animation: counting-pulse 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
.counting-btn { -webkit-tap-highlight-color: transparent; }
.counting-btn:focus-visible { outline: 3px solid ${LEARN.color.blue}; outline-offset: 3px; }
.counting-root::-webkit-scrollbar { width: 0; height: 0; }
.counting-target { position: relative; }
.counting-target::after { content: ""; position: absolute; inset: -5px; border-radius: inherit; border: 3px solid transparent; pointer-events: none; transition: border-color 140ms; }
@media (hover: hover) and (pointer: fine) {
  .counting-target:not(:disabled):not([aria-disabled="true"]):hover::after { border-color: var(--target-accent, ${LEARN.color.blue}); }
}
@media (prefers-reduced-motion: reduce) { .counting-float, .counting-shake, .counting-pulse { animation: none; } }
`;

function CountingStyles() {
  useEffect(() => {
    if (typeof document === "undefined" || document.getElementById(STYLE_ID)) return;
    const tag = document.createElement("style");
    tag.id = STYLE_ID;
    tag.textContent = STYLE;
    document.head.appendChild(tag);
  }, []);
  return null;
}

/**
 * The engine root: fills the arena (flex column), scrolls inside itself if
 * content ever exceeds the space, hosts absolute overlays (confetti, ghost).
 */
export function CountingRoot({ children, style, gap = 12 }) {
  return (
    <div
      className="counting-root"
      style={{
        flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column",
        overflow: "auto", overscrollBehavior: "contain", gap, padding: 6,
        fontFamily: LEARN.font, color: LEARN.color.ink,
        ...style,
      }}
    >
      <CountingStyles />
      {children}
    </div>
  );
}

/**
 * The illustrated "ground" every scene panel stands on: a fading dotted
 * grid, a scalloped grass line and a soft wash of the engine accent along
 * the bottom, so pieces read as objects standing in a place.
 */
function PanelGround({ tint }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute", left: 0, right: 0, bottom: 0, zIndex: -1, pointerEvents: "none",
        height: "min(36%, calc(var(--tile) * 1.7))", overflow: "hidden",
        borderRadius: `0 0 ${LEARN.radius.lg - 2}px ${LEARN.radius.lg - 2}px`,
      }}
    >
      <div
        style={{
          position: "absolute", inset: 0,
          backgroundImage: `radial-gradient(circle, ${hexAlpha(tint, 0.34)} 1.4px, transparent 2px)`,
          backgroundSize: "calc(var(--tile) * 0.24) calc(var(--tile) * 0.24)", backgroundPosition: "center bottom",
          WebkitMaskImage: "linear-gradient(to top, rgba(0,0,0,0.95) 30%, transparent)",
          maskImage: "linear-gradient(to top, rgba(0,0,0,0.95) 30%, transparent)",
        }}
      />
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "calc(var(--tile) * 0.4)", background: `linear-gradient(180deg, ${hexAlpha(tint, 0.08)}, ${hexAlpha(tint, 0.24)})` }} />
      <svg
        viewBox="0 0 400 12"
        preserveAspectRatio="none"
        style={{ position: "absolute", left: "2%", right: "2%", width: "96%", bottom: "calc(var(--tile) * 0.36)", height: "calc(var(--tile) * 0.12)", overflow: "visible" }}
      >
        <path
          d="M0 9 Q 12 3 25 9 T 50 9 T 75 9 T 100 9 T 125 9 T 150 9 T 175 9 T 200 9 T 225 9 T 250 9 T 275 9 T 300 9 T 325 9 T 350 9 T 375 9 T 400 9"
          fill="none" stroke={hexAlpha(tint, 0.6)} strokeWidth="3" strokeLinecap="round" vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

/**
 * Header chip pinned inside a panel's top-left: the round's target, e.g.
 * lead="Pack" value={2} text="blueberries", or just text="Tap each one".
 */
export function PanelChip({ lead, value, text, accent = LEARN.color.blue, style }) {
  return (
    <div
      style={{
        position: "absolute", top: "calc(var(--tile) * 0.14)", left: "calc(var(--tile) * 0.14)", zIndex: 2,
        display: "inline-flex", alignItems: "center", gap: "calc(var(--tile) * 0.1)",
        padding: value != null ? "4px 14px 4px 5px" : "6px 16px", minHeight: "calc(var(--tile) * 0.5)", maxWidth: "calc(100% - var(--tile) * 0.28)", boxSizing: "border-box",
        borderRadius: 999, background: "#fff", border: `2px solid ${hexAlpha(accent, 0.35)}`,
        boxShadow: `0 3px 0 ${hexAlpha(accent, 0.28)}, 0 8px 18px rgba(11,26,51,0.10)`,
        fontFamily: LEARN.font, fontWeight: 700, color: LEARN.color.ink, fontSize: "clamp(15px, calc(var(--tile) * 0.19), 19px)", whiteSpace: "nowrap",
        ...style,
      }}
    >
      {lead && <span style={{ color: LEARN.color.muted, paddingLeft: 8 }}>{lead}</span>}
      {value != null && (
        <span
          style={{
            minWidth: "calc(var(--tile) * 0.42)", height: "calc(var(--tile) * 0.42)", padding: "0 8px", borderRadius: 999, display: "grid", placeItems: "center",
            background: accent, color: LEARN.color.ink, fontSize: "clamp(18px, calc(var(--tile) * 0.24), 24px)", fontVariantNumeric: "tabular-nums",
            boxShadow: "inset 0 -3px 0 rgba(11,26,51,0.14)",
          }}
        >
          {value}
        </span>
      )}
      {text && <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{text}</span>}
    </div>
  );
}

/** The shared round counter / progress style: a small frosted pill top-right. */
export function CornerPill({ children, live = true, style }) {
  return (
    <div
      aria-live={live ? "polite" : undefined}
      style={{
        position: "absolute", top: "calc(var(--tile) * 0.14)", right: "calc(var(--tile) * 0.14)", zIndex: 2,
        display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", minHeight: "calc(var(--tile) * 0.42)", boxSizing: "border-box",
        borderRadius: 999, background: "rgba(255,255,255,0.82)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        border: `2px solid ${LEARN.color.line}`, boxShadow: "0 4px 12px rgba(11,26,51,0.08)",
        fontFamily: LEARN.font, fontWeight: 700, fontSize: "clamp(13px, calc(var(--tile) * 0.16), 16px)", color: LEARN.color.muted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Frosted scene panel. `grow` makes it the flexible middle of the root;
 * `tint` is the engine accent (inner gradient + ground); `chip` and
 * `corner` are pinned top-left / top-right (content pads down for them).
 */
export function CountingPanel({ children, style, grow = false, tint, chip, corner, ground = true, ...rest }) {
  const accent = tint || LEARN.course.counting.accent;
  const hasHeader = Boolean(chip || corner);
  return (
    <div
      {...rest}
      style={{
        ...COUNTING_PANEL,
        background: `radial-gradient(ellipse at 50% -10%, ${hexAlpha(accent, 0.16)}, transparent 55%), linear-gradient(180deg, rgba(255,255,255,0.97) 0%, rgba(255,255,255,0.93) 60%, ${hexAlpha(accent, 0.12)} 100%)`,
        ...(grow ? { flex: 1, minHeight: 0 } : null),
        position: "relative", isolation: "isolate", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "clamp(8px, 2vmin, 18px)",
        ...(hasHeader ? { paddingTop: "calc(var(--tile) * 0.72)" } : null),
        ...(ground ? { paddingBottom: "max(clamp(8px, 2vmin, 18px), calc(var(--tile) * 0.3))" } : null),
        ...style,
      }}
    >
      {ground && <PanelGround tint={accent} />}
      {chip}
      {corner}
      {children}
    </div>
  );
}

/**
 * The supply tray under a scene: a wooden shelf the loose pieces stand on.
 * Honey-oak plank with a faint grain, a highlight along the top and a solid
 * front edge, so pieces read as sitting on a ledge rather than in a panel.
 */
export function SupplyShelf({ children, label, style, gap = "calc(var(--tile) * 0.15)", ...rest }) {
  const lip = "calc(var(--tile) * 0.16)";
  return (
    <div
      role="group"
      aria-label={label}
      {...rest}
      style={{
        position: "relative", flexShrink: 0, width: "100%", boxSizing: "border-box", isolation: "isolate",
        display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "flex-end", gap,
        padding: `calc(var(--tile) * 0.14) calc(var(--tile) * 0.2) calc(var(--tile) * 0.12 + ${lip})`,
        borderRadius: LEARN.radius.md,
        background: [
          `repeating-linear-gradient(90deg, transparent 0 calc(var(--tile) * 0.55), ${hexAlpha(WOOD, 0.05)} calc(var(--tile) * 0.55) calc(var(--tile) * 0.62), transparent calc(var(--tile) * 0.62) calc(var(--tile) * 1.3), ${hexAlpha(WOOD, 0.07)} calc(var(--tile) * 1.3) calc(var(--tile) * 1.34))`,
          `linear-gradient(180deg, ${hexAlpha(WOOD, 0.2)} 0%, ${hexAlpha(WOOD, 0.3)} 100%)`,
          LEARN.color.white,
        ].join(", "),
        border: `2px solid ${hexAlpha(WOOD, 0.35)}`,
        boxShadow: `inset 0 3px 0 rgba(255,255,255,0.55), 0 calc(var(--tile) * 0.08) 0 ${darkenHex(WOOD, 0.3)}, ${LEARN.shadow.card}`,
        ...style,
      }}
    >
      {children}
      {/* the front edge of the plank */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute", left: 0, right: 0, bottom: 0, height: lip, zIndex: -1,
          background: `linear-gradient(180deg, ${hexAlpha(WOOD, 0.45)}, ${hexAlpha(WOOD, 0.62)})`,
          borderTop: `2px solid ${hexAlpha(LEARN.color.white, 0.5)}`,
          borderRadius: `0 0 ${LEARN.radius.md - 2}px ${LEARN.radius.md - 2}px`,
        }}
      />
    </div>
  );
}

// ── The piece ─────────────────────────────────────────────────────────────

/**
 * One counting object. `bare` (default) is just the cutout (pieces that sit
 * in a basket, a ten-frame or a pile); `sticker` is a white rounded tile of
 * `size` with the cutout at ~80% inside, for pieces that stand on their own.
 */
export function CountingPiece({
  asset,
  size = "var(--tile)",
  variant = "bare",
  phase = 0,
  floats = false,
  reduceMotion = false,
  label,
  ring,          // accent colour for a highlight ring (selected / counted)
  dim = false,
  shadow = true, // soft drop shadow so the cutout reads as an object on the ground
  lifted = false,
  style,
  imgStyle,
}) {
  const key = `counting/activities/counting_piece_${asset}`;
  const float = floats && !reduceMotion;
  const delay = (phase % 5) * 0.12;
  const duration = 1.7 + (phase % 4) * 0.16;
  const sticker = variant === "sticker";
  const castShadow = shadow && !dim;
  const cutoutShadow = castShadow
    ? sticker
      ? `drop-shadow(0 calc(var(--tile) * 0.03) calc(var(--tile) * 0.04) rgba(11,26,51,0.18))`
      : `drop-shadow(0 calc(var(--tile) * ${lifted ? 0.1 : 0.06}) calc(var(--tile) * ${lifted ? 0.1 : 0.07}) rgba(11,26,51,${lifted ? 0.3 : 0.24}))`
    : "none";
  return (
    <span
      aria-label={label}
      role={label ? "img" : undefined}
      aria-hidden={label ? undefined : "true"}
      className={float ? "counting-float" : undefined}
      style={{
        position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: size, height: size, flexShrink: 0, boxSizing: "border-box",
        borderRadius: sticker ? "22%" : 0,
        background: sticker ? CARD_FACE : "transparent",
        border: sticker ? `2px solid ${ring || LEARN.color.line}` : "none",
        boxShadow: sticker
          ? `${ring ? `0 0 0 3px ${hexAlpha(ring, 0.35)}, ` : ""}0 3px 0 rgba(11,26,51,0.06), 0 calc(var(--tile) * ${lifted ? 0.2 : 0.12}) calc(var(--tile) * ${lifted ? 0.28 : 0.2}) rgba(11,26,51,${lifted ? 0.2 : 0.14})`
          : ring ? `0 0 0 3px ${ring}` : "none",
        opacity: dim ? 0.35 : 1,
        filter: dim ? "saturate(0.2)" : "none",
        transition: "box-shadow 200ms, border-color 160ms, opacity 200ms, filter 200ms",
        animationDelay: float ? `${delay}s` : undefined,
        animationDuration: float ? `${duration}s` : undefined,
        ...style,
      }}
    >
      <img
        src={COUNTING_PIECE(asset)}
        srcSet={learnAssetSrcSet(key)}
        alt=""
        draggable={false}
        decoding="async"
        style={{
          width: sticker ? "80%" : "100%", height: sticker ? "80%" : "100%", objectFit: "contain",
          pointerEvents: "none", userSelect: "none", filter: cutoutShadow, transition: "filter 200ms", ...imgStyle,
        }}
      />
    </span>
  );
}

/** Numbered badge that pops onto the corner of a counted piece. */
export function CountBadge({ n, accent = LEARN.color.sun, reduceMotion = false, style }) {
  return (
    <motion.span
      aria-hidden="true"
      initial={reduceMotion ? false : { scale: 0, rotate: -20 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 520, damping: 18 }}
      style={{
        position: "absolute", top: "calc(var(--tile) * -0.08)", right: "calc(var(--tile) * -0.08)",
        minWidth: "max(30px, calc(var(--tile) * 0.4))", height: "max(30px, calc(var(--tile) * 0.4))", padding: "0 8px", borderRadius: 999,
        background: accent, color: LEARN.color.ink, fontFamily: LEARN.font, fontWeight: 700, fontSize: "max(17px, calc(var(--tile) * 0.22))",
        display: "grid", placeItems: "center", boxShadow: "0 3px 0 rgba(11,26,51,0.18), 0 6px 14px rgba(11,26,51,0.16)", border: "2.5px solid #fff",
        fontVariantNumeric: "tabular-nums", zIndex: 2, transformOrigin: "50% 50%", ...style,
      }}
    >
      {n}
    </motion.span>
  );
}

/**
 * A set of identical (or mixed) pieces that fills the box it is given: the
 * field is measured and each piece is sized so the whole set fits, as big as
 * `scale x --tile` (see `pieceScale`), never under 56px. With `countable`,
 * each piece is a tap-to-count button: counts once, speaks the number word,
 * wears a badge. `columns` pins the column count (a single showing column).
 */
export function CountingPieceGrid({
  count,
  asset,
  assets,
  columns,
  scale,
  gapRatio,
  variant = "bare",
  floats = false,
  reduceMotion = false,
  countable = false,
  speak,
  sfx,
  accent = LEARN.color.sun,
  labelPrefix,
  pop = true,
  style,
}) {
  const [tagged, setTagged] = useState([]);
  const { ref, size, cols, gap } = useFitPieceSize({ count, scale, gapRatio, maxColumns: columns });
  const tap = (i) => {
    if (!countable || tagged.includes(i)) return;
    const next = [...tagged, i];
    setTagged(next);
    sfx?.("pop");
    speak?.(countingSpokenWord(next.length), { interrupt: true });
  };
  return (
    <PieceField fieldRef={ref} size={size} gap={gap} cols={cols} style={style}>
      {Array.from({ length: count }, (_, i) => {
        const name = assets ? assets[i % assets.length] : asset;
        const position = tagged.indexOf(i);
        const noun = PIECE_NOUN[name] || "item";
        const counted = position >= 0;
        const inner = (
          <CountingPiece asset={name} size={size} variant={variant} phase={i} floats={floats && !counted} reduceMotion={reduceMotion} ring={counted ? accent : undefined} lifted={counted} />
        );
        const body = !reduceMotion ? (
          <motion.span
            initial={pop ? { scale: 0, opacity: 0 } : false}
            animate={{ scale: 1, opacity: 1, y: counted ? -6 : 0 }}
            transition={{ ...POP_SPRING, delay: pop && !counted ? Math.min(i * 0.05, 0.6) : 0 }}
            style={{ display: "inline-flex", position: "relative" }}
          >
            {inner}
            {counted && <CountBadge n={position + 1} accent={accent} reduceMotion={reduceMotion} />}
          </motion.span>
        ) : (
          <span style={{ display: "inline-flex", position: "relative" }}>
            {inner}
            {counted && <CountBadge n={position + 1} accent={accent} reduceMotion />}
          </span>
        );
        if (!countable) return <span key={i} aria-label={labelPrefix ? `${labelPrefix} ${i + 1}` : `${noun} ${i + 1}`} role="img" style={{ display: "inline-flex" }}>{body}</span>;
        return (
          <motion.button
            key={i}
            type="button"
            className="counting-btn"
            aria-label={position >= 0 ? `${noun} ${i + 1}, counted ${position + 1}` : `${noun} ${i + 1}`}
            onClick={() => tap(i)}
            whileHover={counted || reduceMotion ? undefined : { scale: 1.06 }}
            whileTap={counted || reduceMotion ? undefined : { scale: 0.94 }}
            style={{ background: "none", border: "none", padding: 0, cursor: position >= 0 ? "default" : "pointer", display: "inline-flex", position: "relative", touchAction: "manipulation", borderRadius: "22%" }}
          >
            {body}
          </motion.button>
        );
      })}
    </PieceField>
  );
}

// ── Number cards ──────────────────────────────────────────────────────────

const CARD_STATE = (accent) => ({
  idle: { border: hexAlpha(accent, 0.45), edge: EDGE_IDLE, bg: CARD_FACE, color: LEARN.color.ink },
  selected: { border: accent, edge: darkenHex(accent, 0.3), bg: `linear-gradient(180deg, #fff, ${hexAlpha(accent, 0.16)})`, color: LEARN.color.ink },
  correct: { border: LEARN.color.mint, edge: LEARN.color.mintDeep, bg: `linear-gradient(180deg, #fff, ${hexAlpha(LEARN.color.mint, 0.22)})`, color: LEARN.color.mintDeep },
  wrong: { border: LEARN.color.coral, edge: LEARN.color.coralDeep, bg: `linear-gradient(180deg, #fff, ${hexAlpha(LEARN.color.coral, 0.16)})`, color: LEARN.color.coralDeep },
  placed: { border: hexAlpha(accent, 0.7), edge: darkenHex(accent, 0.3), bg: CARD_FACE, color: LEARN.color.ink },
});

/**
 * Card faces: a choice tile is at least 1.5 tiles wide with a 34px numeral
 * and a 3D bottom edge; the big seated cards of a sequence a touch larger.
 */
const CARD_DIMS = {
  md: { minWidth: "calc(var(--tile) * 1.5)", height: "calc(var(--tile) * 1.3)", fontSize: "clamp(30px, calc(var(--tile) * 0.4), 34px)" },
  lg: { minWidth: "calc(var(--tile) * 1.7)", height: "calc(var(--tile) * 1.45)", fontSize: "clamp(32px, calc(var(--tile) * 0.44), 40px)" },
};
const CARD_EDGE = "calc(var(--tile) * 0.07)";

/**
 * Big tappable numeral card. `as="div"` renders the same face without button
 * semantics (drag ghosts, cards already seated in a slot).
 */
export function NumberCard({
  children,
  label,
  onClick,
  state = "idle",
  accent = LEARN.color.blue,
  disabled = false,
  reduceMotion = false,
  as = "button",
  size = "md",
  style,
  wrapStyle,
  ...rest
}) {
  const s = CARD_STATE(accent)[state] || CARD_STATE(accent).idle;
  const Tag = as === "div" ? motion.div : motion.button;
  const dims = size === "lg" ? CARD_DIMS.lg : CARD_DIMS.md;
  const text = String(children);
  return (
    <ShakeWrap shake={state === "wrong"} reduceMotion={reduceMotion} style={{ display: "inline-flex", ...wrapStyle }}>
      <Tag
        type={as === "div" ? undefined : "button"}
        className="counting-btn"
        aria-label={as === "div" ? undefined : label ?? text}
        onClick={disabled ? undefined : onClick}
        disabled={as === "div" ? undefined : disabled}
        whileTap={!disabled && !reduceMotion && as !== "div" ? { y: 4, scale: 0.97 } : undefined}
        whileHover={!disabled && !reduceMotion && as !== "div" ? { y: -3 } : undefined}
        style={{
          ...dims, minHeight: 56, padding: "0 14px", borderRadius: LEARN.radius.md, boxSizing: "border-box",
          border: `3px solid ${s.border}`, background: s.bg, color: s.color,
          boxShadow: `inset 0 2px 0 rgba(255,255,255,0.9), 0 ${CARD_EDGE} 0 ${s.edge}, 0 calc(var(--tile) * 0.14) calc(var(--tile) * 0.22) rgba(11,26,51,0.12)`,
          fontFamily: LEARN.font, fontWeight: 700,
          fontVariantNumeric: "tabular-nums", lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center",
          cursor: disabled || as === "div" ? "default" : "pointer", userSelect: "none",
          transition: "border-color 160ms, background 160ms, box-shadow 160ms",
          ...style,
        }}
        {...rest}
      >
        {children}
      </Tag>
    </ShakeWrap>
  );
}

/** Empty numbered place a card can be dropped into. */
export function CardSlot({ children, hover, accent = LEARN.color.blue, filled = false, label, slotRef, onClick, size = "md", style }) {
  const dims = { minWidth: CARD_DIMS[size === "lg" ? "lg" : "md"].minWidth, height: CARD_DIMS[size === "lg" ? "lg" : "md"].height };
  return (
    <div
      ref={slotRef}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={label}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); } } : undefined}
      className={onClick ? "counting-btn counting-target" : "counting-target"}
      style={{
        ...dims, minHeight: 56, borderRadius: LEARN.radius.md, boxSizing: "border-box", "--target-accent": accent,
        border: filled ? "3px solid transparent" : `3px dashed ${hover ? accent : hexAlpha(accent, 0.6)}`,
        background: filled ? "transparent" : hover ? hexAlpha(accent, 0.22) : hexAlpha(accent, 0.08),
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        transform: hover ? "scale(1.06)" : "scale(1)", transition: "transform 140ms, background 140ms, border-color 140ms",
        cursor: onClick ? "pointer" : "default",
        fontFamily: LEARN.font, fontWeight: 700, color: hexAlpha(accent, 0.9),
        fontSize: CARD_DIMS[size === "lg" ? "lg" : "md"].fontSize,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Readouts ──────────────────────────────────────────────────────────────

/** Ink for big accent digits: vivid accents stay vivid, sun yellow turns gold. */
export function counterInk(accent) {
  return darkenHex(accent, 0.22);
}

/**
 * The big friendly counter that sits under a scene panel: ~40px accent
 * digits, an optional "of N", and the number word beside them.
 */
export function BigCounter({ value = 0, total, word, accent = LEARN.color.sun, placeholder = "Tap each one", style }) {
  const has = value > 0;
  return (
    <div
      aria-live="polite"
      aria-label={total ? `${value} of ${total}` : String(value)}
      style={{
        alignSelf: "center", display: "inline-flex", alignItems: "center", gap: "calc(var(--tile) * 0.14)", padding: "4px 20px 4px 16px", minHeight: "calc(var(--tile) * 0.72)",
        ...COUNTING_PANEL, borderRadius: 999, boxSizing: "border-box", maxWidth: "100%", ...style,
      }}
    >
      <span
        key={value}
        className={has ? "counting-pulse" : undefined}
        style={{
          fontWeight: 700, fontSize: "clamp(34px, calc(var(--tile) * 0.42), 44px)", lineHeight: 1, letterSpacing: "-0.01em",
          color: has ? counterInk(accent) : hexAlpha(LEARN.color.ink, 0.28), fontVariantNumeric: "tabular-nums", transition: "color 200ms",
        }}
      >
        {value}
      </span>
      {total != null && (
        <span style={{ fontWeight: 700, fontSize: "clamp(16px, calc(var(--tile) * 0.2), 20px)", color: LEARN.color.muted, whiteSpace: "nowrap" }}>of {total}</span>
      )}
      <span style={{ fontWeight: 700, fontSize: "clamp(17px, calc(var(--tile) * 0.22), 22px)", color: has ? LEARN.color.ink : LEARN.color.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {has ? (word ?? countingSpokenWord(value)) : placeholder}
      </span>
    </div>
  );
}

// ── Feedback wrappers ─────────────────────────────────────────────────────

export function ShakeWrap({ shake, reduceMotion, children, style, ...rest }) {
  return (
    <div {...rest} className={shake && !reduceMotion ? "counting-shake" : undefined} style={style}>
      {children}
    </div>
  );
}

// ── Hooks ─────────────────────────────────────────────────────────────────

export function useCompleteOnce(onComplete) {
  const done = useRef(false);
  return useCallback(() => {
    if (done.current) return;
    done.current = true;
    onComplete?.();
  }, [onComplete]);
}

export function useWrongFlash(ms = 460) {
  const [wrongId, setWrongId] = useState(null);
  const timer = useRef(null);
  const flash = useCallback((id = "global") => {
    setWrongId(id);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setWrongId(null), ms);
  }, [ms]);
  useEffect(() => () => timer.current && clearTimeout(timer.current), []);
  return { wrongId, flash, isWrong: (id = "global") => wrongId === id };
}

/**
 * The shared drag engine plus a tap guard: browsers fire `click` on the
 * source after a captured drag, so `guardTap(fn)` ignores the click that
 * follows a real drag while leaving plain taps (< 6px) untouched. The guard
 * arms when a drag activates and clears on the next pointerdown.
 */
export function useCountingDrag({ reduceMotion = false, sfx, onDrop } = {}) {
  const moved = useRef(false);
  const dropRef = useRef(onDrop);
  dropRef.current = onDrop;
  const onPickup = useCallback(() => {
    moved.current = true;
    sfx?.("pickup", { volume: 0.5 });
  }, [sfx]);
  const handleDrop = useCallback((info) => (dropRef.current ? dropRef.current(info) : false), []);
  const engine = useDragEngine({ reduceMotion, onPickup, onDrop: handleDrop });
  const baseDragProps = engine.dragProps;
  const dragProps = useCallback((payload, render) => {
    const p = baseDragProps(payload, render);
    return {
      ...p,
      onPointerDown: (e) => {
        moved.current = false;
        p.onPointerDown(e);
      },
    };
  }, [baseDragProps]);
  const guardTap = useCallback((fn) => (e) => {
    if (moved.current) return;
    fn?.(e);
  }, []);
  return { ...engine, dragProps, guardTap };
}

/** Bare draggable button styles (hit target, no chrome). */
export const DRAG_BUTTON = {
  background: "none", border: "none", padding: 4, margin: 0, cursor: "grab", display: "inline-flex", position: "relative",
  borderRadius: 24, WebkitTapHighlightColor: "transparent",
};

/** Inline SVG eye (peek / reveal affordances). */
export function EyeIcon({ size = 18, color = "currentColor", stroke = 2.4 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Inline SVG check mark (no text-as-icon). */
export function CheckIcon({ size = 16, color = "#fff", stroke = 3.2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}
